// src/features/goals/services/goalsService.js
// Business logic and Firestore operations for Goals feature

import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  limit
} from 'firebase/firestore';
import { db, COLLECTIONS } from '@/shared/utils/core/firebase-config';
import { DateTime } from 'luxon';

// ═══════════════════════════════════════════════════════════════
// GOAL SYSTEM CONSTANTS
// ═══════════════════════════════════════════════════════════════

export const GOAL_TYPES = {
  HABIT: 'habit',
  TARGET: 'target',
  AVERAGE: 'average',
  MILESTONE: 'milestone',
};

export const TRACKING_SOURCES = {
  MANUAL: 'manual',
  JOURNAL_COUNT: 'journal_count',
  JOURNAL_WORDCOUNT: 'journal_wordcount',
  MOOD_SCORE: 'mood_score',
};

export const GOAL_CATEGORIES = [
  { id: 'mindfulness', label: 'Mindfulness', icon: 'brain', color: '#a78bfa' },
  { id: 'health', label: 'Health', icon: 'heart-pulse', color: '#f87171' },
  { id: 'creativity', label: 'Creativity', icon: 'palette', color: '#fb923c' },
  { id: 'selfcare', label: 'Self Care', icon: 'sparkles', color: '#34d399' },
  { id: 'other', label: 'Other', icon: 'circle-dot', color: '#94a3b8' },
];

// Grace period: completions between midnight and 3 AM count for previous day
export const GRACE_PERIOD_HOURS = 3;

// Progress history subcollection path: goals/{goalId}/progressHistory/{dateId}
export const PROGRESS_HISTORY_SUBCOLLECTION = 'progressHistory';

// Legacy type mapping for backward compatibility
const LEGACY_TYPE_MAP = {
  journaling: { goalType: 'habit', trackingSource: 'journal_count' },
  personal: { goalType: 'habit', trackingSource: 'manual' },
};

// Mood name to numeric score mapping
const MOOD_SCORES = {
  happy: 9, excited: 9, grateful: 8, calm: 7, content: 7,
  neutral: 5, anxious: 3, sad: 3, angry: 2, stressed: 2
};

// ═══════════════════════════════════════════════════════════════
// NORMALIZATION (backward compatibility)
// ═══════════════════════════════════════════════════════════════

/**
 * Normalize a goal from Firestore to the new type system.
 * Maps legacy 'journaling'/'personal' goalType to new types.
 * Does NOT write back to Firestore — purely in-memory transformation.
 */
export function normalizeGoal(goal) {
  if (!goal) return goal;
  const normalized = { ...goal };

  // Map legacy types
  if (goal.goalType in LEGACY_TYPE_MAP) {
    const mapping = LEGACY_TYPE_MAP[goal.goalType];
    normalized._originalGoalType = goal.goalType;
    normalized.goalType = mapping.goalType;
    normalized.trackingSource = mapping.trackingSource;
  }

  // Defaults for new fields
  normalized.trackingSource = normalized.trackingSource || 'manual';
  normalized.category = normalized.category || 'other';
  normalized.milestones = normalized.milestones || null;

  return normalized;
}

// ═══════════════════════════════════════════════════════════════
// STREAK HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Apply 3 AM grace period: completions between midnight and 3 AM count as previous day.
 */
export function getEffectiveDate(date) {
  const dt = DateTime.fromJSDate(date);
  if (dt.hour < GRACE_PERIOD_HOURS) {
    return dt.minus({ days: 1 }).startOf('day');
  }
  return dt.startOf('day');
}

// ═══════════════════════════════════════════════════════════════
// FIRESTORE OPERATIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Subscribe to user's goals with real-time updates.
 * Goals are normalized on read for backward compatibility.
 */
export function subscribeToGoals(userId, callback) {
  if (!userId) {
    callback([]);
    return () => {};
  }

  const q = query(
    collection(db, COLLECTIONS.GOALS),
    where('uid', '==', userId),
    where('status', 'in', ['active', 'completed', 'archived']),
    limit(50)
  );

  const unsubscribe = onSnapshot(q, (snap) => {
    const goalsData = snap.docs.map(d => normalizeGoal({ id: d.id, ...d.data() }));
    callback(goalsData);
  }, (error) => {
    console.error('[goalsService] subscribeToGoals error:', error.code, error.message);
  });

  return unsubscribe;
}

/**
 * Create a new goal
 * @param {string} userId - User ID
 * @param {Object} goalData - Goal configuration
 */
export async function createGoal(userId, goalData) {
  if (!userId) throw new Error('User ID is required');

  // Convert endDate string (from date input) to Firestore Timestamp
  let endDate = null;
  if (goalData.endDate && typeof goalData.endDate === 'string') {
    endDate = Timestamp.fromDate(new Date(goalData.endDate));
  } else if (goalData.endDate instanceof Date) {
    endDate = Timestamp.fromDate(goalData.endDate);
  }

  const goal = {
    uid: userId,
    title: goalData.title,
    description: goalData.description || null,
    goalType: goalData.goalType || 'habit',
    targetType: goalData.targetType || 'frequency',
    targetValue: goalData.targetValue || 1,
    timeframe: goalData.timeframe || 'daily',
    trackingSource: goalData.trackingSource || 'manual',
    category: goalData.category || 'other',
    startDate: goalData.startDate || serverTimestamp(),
    endDate,
    milestones: goalData.milestones || null,
    status: 'active',
    streak: 0,
    longestStreak: 0,
    lastCompleted: null,
    lastChecked: null,
    completedAt: null,
    createdAt: serverTimestamp()
  };

  return await addDoc(collection(db, COLLECTIONS.GOALS), goal);
}

/**
 * Update an existing goal
 */
export async function updateGoal(goalId, updates) {
  if (!goalId) throw new Error('Goal ID is required');

  const goalRef = doc(db, COLLECTIONS.GOALS, goalId);
  await updateDoc(goalRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
}

/**
 * Delete a goal permanently
 */
export async function deleteGoal(goalId) {
  if (!goalId) throw new Error('Goal ID is required');

  const goalRef = doc(db, COLLECTIONS.GOALS, goalId);
  await deleteDoc(goalRef);
}

/**
 * Archive a goal (keeps history)
 */
export async function archiveGoal(goalId) {
  return await updateGoal(goalId, { status: 'archived' });
}

// ═══════════════════════════════════════════════════════════════
// PROGRESS TRACKING
// ═══════════════════════════════════════════════════════════════

/**
 * Log progress for a goal and update streak.
 * Supports 3 AM grace period and streak freezes.
 */
export async function logGoalProgress(goalId, goal, value = 1, date = new Date()) {
  if (!goalId || !goal) throw new Error('Goal ID and goal object are required');

  const effectiveDate = getEffectiveDate(date);
  const lastCompleted = goal.lastCompleted
    ? DateTime.fromJSDate(goal.lastCompleted?.toDate ? goal.lastCompleted.toDate() : goal.lastCompleted).startOf('day')
    : null;

  let newStreak = goal.streak || 0;
  let newLongestStreak = goal.longestStreak || 0;

  if (lastCompleted) {
    const daysDiff = Math.round(effectiveDate.diff(lastCompleted, 'days').days);

    if (daysDiff === 1) {
      // Consecutive day — increment streak
      newStreak += 1;
    } else if (daysDiff > 1) {
      // Streak broken — reset
      newStreak = 1;
    }
    // daysDiff <= 0 (same day or past) — keep current streak
  } else {
    // First completion
    newStreak = 1;
  }

  newLongestStreak = Math.max(newLongestStreak, newStreak);

  await updateGoal(goalId, {
    streak: newStreak,
    longestStreak: newLongestStreak,
    lastCompleted: date,
  });

  // Dual write: persist to progressHistory subcollection
  const effectiveDateStr = effectiveDate.toFormat
    ? effectiveDate.toFormat('yyyy-MM-dd')
    : effectiveDate._date.toISOString().split('T')[0];

  const historyRef = doc(
    db, COLLECTIONS.GOALS, goalId, PROGRESS_HISTORY_SUBCOLLECTION, effectiveDateStr
  );

  await setDoc(historyRef, {
    uid: goal.uid,
    completedAt: Timestamp.fromDate(date),
    value,
    streakAtCompletion: newStreak,
    createdAt: serverTimestamp(),
  });
}

/**
 * Calculate goal progress for current timeframe.
 * Handles all 4 goal types and all tracking sources.
 * @returns {{ current: number, target: number, percentage: number, isComplete: boolean, milestones?: Array }}
 */
export function calculateGoalProgress(goal, journalEntries = []) {
  if (!goal) return { current: 0, target: 1, percentage: 0, isComplete: false };

  const goalType = goal.goalType;
  const trackingSource = goal.trackingSource || 'manual';

  // Milestone goals: count completed sub-tasks
  if (goalType === GOAL_TYPES.MILESTONE) {
    const milestones = goal.milestones || [];
    const completed = milestones.filter(m => m.completed).length;
    const total = milestones.length || 1;
    const percentage = Math.min(Math.round((completed / total) * 100), 100);
    return {
      current: completed,
      target: total,
      percentage,
      isComplete: completed >= total,
      milestones,
    };
  }

  // Determine period start based on timeframe
  const now = DateTime.now();
  let startOfPeriod;
  switch (goal.timeframe) {
    case 'daily':
      startOfPeriod = now.startOf('day');
      break;
    case 'weekly':
      startOfPeriod = now.startOf('week');
      break;
    case 'monthly':
      startOfPeriod = now.startOf('month');
      break;
    default:
      startOfPeriod = now.startOf('day');
  }

  let currentProgress = 0;

  // Target goals use cumulative progress (not per-period)
  const isCumulative = goalType === GOAL_TYPES.TARGET;

  // Helper: get entries in current period
  const getEntriesInPeriod = () => journalEntries.filter(entry => {
    if (!entry.createdAt) return false;
    const entryDate = DateTime.fromJSDate(
      entry.createdAt.toDate ? entry.createdAt.toDate() : new Date(entry.createdAt)
    );
    return entryDate >= startOfPeriod;
  });

  // Calculate progress based on tracking source
  switch (trackingSource) {
    case TRACKING_SOURCES.JOURNAL_COUNT: {
      const entries = isCumulative ? journalEntries : getEntriesInPeriod();
      currentProgress = entries.length;
      break;
    }

    case TRACKING_SOURCES.JOURNAL_WORDCOUNT: {
      const entries = isCumulative ? journalEntries : getEntriesInPeriod();
      currentProgress = entries.reduce((sum, entry) => {
        const text = entry.content || entry.extractedText || '';
        return sum + text.split(/\s+/).filter(Boolean).length;
      }, 0);
      break;
    }

    case TRACKING_SOURCES.MOOD_SCORE: {
      // Average mood score in period (for average goal type)
      const periodEntries = getEntriesInPeriod().filter(e => e.mood);
      if (periodEntries.length > 0) {
        const totalScore = periodEntries.reduce(
          (sum, e) => sum + (MOOD_SCORES[e.mood] || 5), 0
        );
        currentProgress = Math.round((totalScore / periodEntries.length) * 10) / 10;
      }
      break;
    }

    case TRACKING_SOURCES.MANUAL:
    default: {
      const lastCompleted = goal.lastCompleted?.toDate
        ? DateTime.fromJSDate(goal.lastCompleted.toDate())
        : null;
      if (lastCompleted && lastCompleted >= startOfPeriod) {
        currentProgress = 1;
      }
      break;
    }
  }

  const targetValue = goal.targetValue || 1;
  const percentage = Math.min(Math.round((currentProgress / targetValue) * 100), 100);

  return {
    current: currentProgress,
    target: targetValue,
    percentage,
    isComplete: currentProgress >= targetValue,
  };
}

// ═══════════════════════════════════════════════════════════════
// PROGRESS HISTORY QUERIES
// ═══════════════════════════════════════════════════════════════

/**
 * Fetch progress history for a goal within a date range.
 * @param {string} goalId
 * @param {string} userId - Required for Firestore security rules
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {Promise<Array>}
 */
export async function getProgressHistory(goalId, userId, startDate, endDate) {
  if (!goalId || !userId) return [];

  const historyRef = collection(
    db, COLLECTIONS.GOALS, goalId, PROGRESS_HISTORY_SUBCOLLECTION
  );

  const q = query(
    historyRef,
    where('uid', '==', userId),
    where('completedAt', '>=', Timestamp.fromDate(startDate)),
    where('completedAt', '<=', Timestamp.fromDate(endDate)),
    orderBy('completedAt', 'asc'),
    limit(200)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      completedAt: data.completedAt?.toDate ? data.completedAt.toDate() : new Date(data.completedAt),
      value: data.value || 1,
      streakAtCompletion: data.streakAtCompletion || 0,
    };
  });
}

/**
 * Subscribe to progress history with real-time updates.
 * @param {string} goalId
 * @param {string} userId - Required for Firestore security rules
 * @param {Date} startDate
 * @param {function} callback
 * @returns {function} unsubscribe
 */
export function subscribeToProgressHistory(goalId, userId, startDate, callback) {
  if (!goalId || !userId) {
    callback([]);
    return () => {};
  }

  const historyRef = collection(
    db, COLLECTIONS.GOALS, goalId, PROGRESS_HISTORY_SUBCOLLECTION
  );

  const q = query(
    historyRef,
    where('uid', '==', userId),
    where('completedAt', '>=', Timestamp.fromDate(startDate)),
    orderBy('completedAt', 'asc'),
    limit(200)
  );

  return onSnapshot(q, (snapshot) => {
    const entries = snapshot.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        completedAt: data.completedAt?.toDate ? data.completedAt.toDate() : new Date(data.completedAt),
        value: data.value || 1,
        streakAtCompletion: data.streakAtCompletion || 0,
      };
    });
    callback(entries);
  }, (error) => {
    // On permission error, return empty and stop loading
    console.warn('Progress history subscription error:', error.message);
    callback([]);
  });
}
